import { useState } from 'react';

import { api } from '../api/client';
import type { ChainVerification, OrgUnitRow, PolicyRow, WebhookDeliveryRow } from '../api/types';
import { Badge, Button, Card, Field, Form, Loading, Metric, Modal, Notice, Select, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';

/**
 * Configuration screens, grouped in one file because each is small and they share nothing
 * but their shape. Splitting them into five files would spread one idea across five places.
 */

/* ── Organisation hierarchy ──────────────────────────────────────────────────── */

export function OrgUnits(): JSX.Element {
  const { can } = useSession();
  const [creating, setCreating] = useState(false);
  const units = useResource(() => api.get<OrgUnitRow[]>('/org-units'), []);

  return (
    <>
      <Card
        title="Organisation"
        actions={can('org.edit') ? <Button onClick={() => setCreating(true)}>Add unit</Button> : undefined}
      >
        {units.loading ? (
          <Loading />
        ) : units.error !== null ? (
          <Notice kind="error">{units.error}</Notice>
        ) : (
          <Table
            rows={units.data ?? []}
            keyOf={(row) => row.id}
            columns={[
              {
                header: 'Unit',
                // Indentation carries the hierarchy. A flat list of names loses the one
                // thing the tree is for.
                render: (row) => (
                  <span style={{ paddingLeft: `${Math.max(0, row.depth - 1) * 18}px` }}>{row.name}</span>
                ),
              },
              { header: 'Code', render: (row) => row.code ?? '—' },
              { header: 'Depth', numeric: true, render: (row) => row.depth },
            ]}
          />
        )}
      </Card>

      {creating && (
        <OrgUnitForm
          units={units.data ?? []}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            units.reload();
          }}
        />
      )}
    </>
  );
}

function OrgUnitForm({
  units,
  onClose,
  onSaved,
}: {
  units: OrgUnitRow[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [parentId, setParentId] = useState('');

  const submit = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post('/org-units', {
        name,
        ...(code === '' ? {} : { code }),
        ...(parentId === '' ? {} : { parentId }),
      });
    });
    if (ok) onSaved();
  };

  return (
    <Modal title="Add organisation unit" onClose={onClose}>
      {error !== null && <Notice kind="error">{error}</Notice>}
      <Form onSubmit={() => void submit()}>
        <Field label="Name" value={name} onChange={setName} required />
        <Field label="Code" value={code} onChange={setCode} />
        <Select
          label="Parent"
          value={parentId}
          onChange={setParentId}
          placeholder="Top level"
          options={units.map((unit) => ({
            value: unit.id,
            label: `${'— '.repeat(Math.max(0, unit.depth - 1))}${unit.name}`,
          }))}
        />
        {/* Depth is not asked for: it is derived from the parent. Accepting it would let
            the tree claim a shape its own links contradict. */}
        <div className="form-actions">
          <Button type="submit" disabled={busy || name === ''}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

/* ── Policies ────────────────────────────────────────────────────────────────── */

/**
 * Settings that change behaviour without a release.
 *
 * A missing or unparseable value falls back to a stated default rather than failing —
 * a typo here must not be able to stop field users submitting work.
 */
export function Policies(): JSX.Element {
  const policies = useResource(() => api.get<PolicyRow[]>('/policies'), []);
  const { run, error, busy } = useAction();
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);

  const save = async (): Promise<void> => {
    if (editing === null) return;
    const ok = await run(async () => {
      await api.put(`/policies/${encodeURIComponent(editing.key)}`, { value: editing.value });
    });
    if (ok) {
      setEditing(null);
      policies.reload();
    }
  };

  return (
    <>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <Card title="Policies">
        {policies.loading ? (
          <Loading />
        ) : policies.error !== null ? (
          <Notice kind="error">{policies.error}</Notice>
        ) : (
          <Table
            rows={policies.data ?? []}
            keyOf={(row) => row.id}
            empty="No policies have been set. Defaults apply."
            columns={[
              { header: 'Key', render: (row) => <code>{row.key}</code> },
              { header: 'Value', render: (row) => row.value },
              { header: 'Description', render: (row) => row.description ?? '—' },
              {
                header: '',
                render: (row) => (
                  <Button variant="secondary" onClick={() => setEditing({ key: row.key, value: row.value })}>
                    Edit
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      {editing !== null && (
        <Modal title={editing.key} onClose={() => setEditing(null)}>
          <Form onSubmit={() => void save()}>
            <Field
              label="Value"
              value={editing.value}
              onChange={(value) => setEditing({ ...editing, value })}
              required
            />
            <div className="form-actions">
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </>
  );
}

/* ── Integrations ────────────────────────────────────────────────────────────── */

export function Webhooks(): JSX.Element {
  const { can } = useSession();
  const [creating, setCreating] = useState(false);
  const abandoned = useResource(() => api.get<WebhookDeliveryRow[]>('/webhooks/abandoned'), []);

  return (
    <>
      {/* See Rules.tsx: the server refuses either way; this stops the screen offering it. */}
      <Card
        title="Integrations"
        actions={can('policy.edit') ? <Button onClick={() => setCreating(true)}>Add subscription</Button> : undefined}
      >
        <p className="muted">
          Events are queued and delivered with a signed payload, retried with backoff. A
          signing secret is supplied once and never read back — this screen cannot show it,
          which is what stops a borrowed session being enough to forge signed payloads.
        </p>
      </Card>

      <Card title="Abandoned deliveries">
        {abandoned.loading ? (
          <Loading />
        ) : abandoned.error !== null ? (
          <Notice kind="error">{abandoned.error}</Notice>
        ) : (
          <Table
            rows={abandoned.data ?? []}
            keyOf={(row) => row.id}
            empty="Nothing has been abandoned."
            columns={[
              { header: 'Event', render: (row) => <code>{row.eventType}</code> },
              { header: 'Attempts', numeric: true, render: (row) => row.attempts },
              { header: 'Status', render: (row) => <Badge tone={row.status}>{row.status}</Badge> },
              { header: 'Last error', render: (row) => row.lastError ?? '—' },
            ]}
          />
        )}
      </Card>

      {creating && <WebhookForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); abandoned.reload(); }} />}
    </>
  );
}

const WEBHOOK_EVENTS = [
  'submission.accepted',
  'submission.flagged',
  'submission.blocked',
  'exception.raised',
  'exception.resolved',
  'capa.raised',
  'capa.closed',
];

function WebhookForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }): JSX.Element {
  const { run, error, busy } = useAction();
  const [name, setName] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<Set<string>>(new Set());

  const toggle = (event: string): void => {
    const next = new Set(events);
    if (next.has(event)) next.delete(event);
    else next.add(event);
    setEvents(next);
  };

  const submit = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post('/webhooks', { name, targetUrl, signingSecret: secret, events: [...events] });
    });
    if (ok) onSaved();
  };

  return (
    <Modal title="Add subscription" onClose={onClose}>
      {error !== null && <Notice kind="error">{error}</Notice>}
      <Form onSubmit={() => void submit()}>
        <Field label="Name" value={name} onChange={setName} required />
        <Field label="Target URL" value={targetUrl} onChange={setTargetUrl} required placeholder="https://" />
        <Field
          label="Signing secret"
          value={secret}
          onChange={setSecret}
          type="password"
          required
          hint="At least 32 characters. Keep a copy — it is never shown again."
        />

        <div className="field">
          <span className="field-label">Events</span>
          <div className="permission-grid">
            {WEBHOOK_EVENTS.map((event) => (
              <label key={event}>
                <input type="checkbox" checked={events.has(event)} onChange={() => toggle(event)} />
                <code>{event}</code>
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <Button type="submit" disabled={busy || name === '' || targetUrl === '' || secret.length < 32 || events.size === 0}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

/* ── Audit trail ─────────────────────────────────────────────────────────────── */

/**
 * Chain verification.
 *
 * The audit trail is hash-chained, so an edited row breaks every link after it. This
 * screen recomputes the chain and reports the first break. A broken chain comes back as a
 * 409 rather than a 200 with a flag, so a monitor can alert on the status code alone.
 */
export function AuditTrail(): JSX.Element {
  const verification = useResource(
    () =>
      api
        .get<ChainVerification>('/audit/verify')
        // A 409 carries the verification body — the failure *is* the answer here.
        .catch((error: unknown) => {
          if (error instanceof Error && 'status' in error && error.status === 409) {
            return { valid: false, linksChecked: 0, brokenAtSequence: null, reason: error.message };
          }
          throw error;
        }),
    [],
  );

  return (
    <Card title="Audit trail integrity" actions={<Button variant="secondary" onClick={verification.reload}>Verify again</Button>}>
      {verification.loading ? (
        <Loading />
      ) : verification.error !== null ? (
        <Notice kind="error">{verification.error}</Notice>
      ) : verification.data === null ? null : (
        <>
          {verification.data.valid ? (
            <Notice kind="success">
              The chain is intact. Every recorded event still hashes to the link that follows it.
            </Notice>
          ) : (
            <Notice kind="error">
              The chain is broken{verification.data.brokenAtSequence === null ? '' : ` at sequence ${verification.data.brokenAtSequence}`}.
              {verification.data.reason === null ? '' : ` ${verification.data.reason}`}
            </Notice>
          )}

          <div className="metrics">
            <Metric label="Links checked" value={verification.data.linksChecked} />
            <Metric
              label="Status"
              value={verification.data.valid ? 'Intact' : 'Broken'}
              tone={verification.data.valid ? 'success' : 'danger'}
            />
          </div>
        </>
      )}
    </Card>
  );
}
